import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import * as authApi from '../lib/authApi.js';
import { setActiveCompanyId as setApiCompanyId } from '../lib/api.js';
import { pickActiveCompanyId, readStoredCompanyId, resolveActiveCompany, writeStoredCompanyId } from '../lib/companySession.js';
export type { User } from '../lib/authApi.js';
import type { User, SessionCompany, SessionActiveCompany } from '../lib/authApi.js';
import type { ActiveCompany } from '../lib/companySession.js';

interface AuthValue {
  user: User | null;
  /* WHAT /api/me REPORTED, verbatim: membership row 0, or the suspended membership when there is
   * no active one. It does NOT follow the picker — the same object comes back under every
   * ?companyId= — so read `activeCompany` below for anything describing the tenant the recruiter
   * is acting as. This field's remaining job is the case `companies` cannot express: it is null
   * for a signed-out visitor, a seeker, an admin and an unprovisioned HR account, and NOT null
   * for a suspended employer, which reports with status 'suspended' so NoCompany can say which
   * company and why rather than showing the never-provisioned screen. A UX branch on
   * already-fetched session state, never a security boundary: the server throws 403 no_company
   * from requireCompany regardless. */
  company: SessionActiveCompany | null;
  /* EVERY active membership, which for a recruiter on two hiring teams is more than one. The
   * picker renders off this, and `activeCompanyId` is always one of its ids or null — never an
   * id from anywhere else, because requireCompany() 403s no_company on an id that is not one of
   * the caller's own rows. */
  companies: SessionCompany[];
  activeCompanyId: string | null;
  /* The company the SCREENS should talk about: the entry from `companies` the picker has
   * selected, falling back to `company` only when the list is empty (the suspended /
   * never-provisioned pair). Read this — not `company` — for anything that describes the tenant
   * the recruiter is currently acting as, because `company` is membership row 0 whatever
   * ?companyId= every other request is carrying. CompanyGate, CompanyIncompleteNotice and the
   * applicant screen's owner check all branch on it. */
  activeCompany: ActiveCompany | null;
  /** Switch tenants. Ignores an id that is not in `companies`. */
  setActiveCompany: (id: string) => void;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<SessionActiveCompany | null>(null);
  const [companies, setCompanies] = useState<SessionCompany[]>([]);
  const [activeCompanyId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /* The ONE place the active company moves, so the three copies of it cannot drift: React state
   * (what the picker renders), api.ts's module-level value (what every request actually carries
   * as ?companyId=) and localStorage (what survives a reload). A setter that updated two of the
   * three would show one tenant and query another. */
  const applyActiveCompany = useCallback((userId: string | null, companyId: string | null) => {
    setActiveId(companyId);
    setApiCompanyId(companyId);
    if (userId && companyId) writeStoredCompanyId(userId, companyId);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const session = await authApi.me();
      setUser(session?.user ?? null);
      setCompany(session?.company ?? null);
      setCompanies(session?.companies ?? []);
      // Re-validated on EVERY load, not just the first: the remembered id outlives the membership
      // it names. pickActiveCompanyId drops a revoked one for entry 0 rather than letting the app
      // pin itself to a ?companyId= that answers 403 no_company on every /hr/* route.
      applyActiveCompany(
        session?.user.id ?? null,
        session ? pickActiveCompanyId(session.companies, readStoredCompanyId(session.user.id)) : null,
      );
    } catch {
      setUser(null);
      setCompany(null);
      setCompanies([]);
      applyActiveCompany(null, null);
    }
  }, [applyActiveCompany]);

  useEffect(() => { void refresh().finally(() => setLoading(false)); }, [refresh]);

  // POST /api/auth/login answers with the user only; the membership comes from /api/me, so
  // re-read the session rather than leaving `company` stale-null — otherwise every recruiter
  // would land on the no-company screen for one render immediately after signing in.
  const login = useCallback(async (email: string, password: string) => {
    const u = await authApi.login(email, password);
    setUser(u);
    await refresh();
    return u;
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } finally {
      setUser(null);
      setCompany(null);
      setCompanies([]);
      // Clearing this is not tidiness: a leftover id would ride the NEXT account's requests as
      // ?companyId=, where it is guaranteed not to be one of their memberships — 403 no_company
      // on every /hr/* route for a recruiter whose own company is perfectly fine.
      applyActiveCompany(null, null);
    }
  }, [applyActiveCompany]);

  const setActiveCompany = useCallback((id: string) => {
    // Only ever an id the session actually holds. Anything else is a 403 no_company waiting to
    // happen, and the picker is the only caller — it renders these same rows.
    if (!user || !companies.some((c) => c.id === id)) return;
    applyActiveCompany(user.id, id);
  }, [user, companies, applyActiveCompany]);

  const activeCompany = useMemo(
    () => resolveActiveCompany(companies, activeCompanyId, company),
    [companies, activeCompanyId, company],
  );

  return (
    <AuthContext.Provider
      value={{ user, company, companies, activeCompanyId, activeCompany, setActiveCompany, loading, login, logout, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
